import {
    Box,
    Button,
    ButtonBase,
    Divider,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { ChevronLeft, Eye, EyeOff, Pencil, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import { useComparisonImages } from '~/hooks/viewer/useComparisonImages';
import {
    isCompleteLandmarkPair,
    LANDMARK_NAME_MAX_LENGTH,
    LandmarkPair,
    LandmarkResidual,
} from '~/domain/landmark/landmark';
import { decomposeColor } from '@mui/material/styles';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { sampleAggregate } from '~/domain/statistics/sampleAggregate';
import { useConfirmClear } from '~/hooks/useConfirmClear';
import { useAddLandmarksToStructureSet } from '~/hooks/useAddLandmarksToStructureSet';
import { useLandmarkController } from '~/hooks/useLandmarkController';
import { useSelectedRegistrationFrames } from '~/hooks/viewer/useSelectedDeformationField';
import ResidualScaleNote from '~/components/Landmarks/ResidualScaleNote';
import { useLandmarkReadout } from '~/hooks/viewer/useLandmarkReadout';
import {
    RunLandmarkResiduals,
    useRunLandmarkResiduals,
} from '~/containers/viewer/explorer-sidebar/WorkflowSidebar/AdaptiveReplanningStep/useRunLandmarkResiduals';
import { usePlaceableFramesOfReference } from '~/hooks/viewer/usePlaceableFramesOfReference';
import { selectedToolState } from '~/state/atoms/toolsAtoms';
import ColorSwatchPicker from '~/components/Inputs/Color/ColorSwatchPicker';
import { LIST_MAX_HEIGHT_PX } from '~/containers/viewer/registration-qa/qaLayout';
import {
    asCm,
    formatMeanWithSpreadCm,
    NOT_AVAILABLE,
} from '~/utils/measurementFormat';

type ByPairId = ReadonlyMap<string, LandmarkResidual>;

function cmOf(residual: LandmarkResidual | undefined): string {
    return residual?.status === 'measured'
        ? asCm(residual.residualMm)
        : NOT_AVAILABLE;
}

// The note shown when a pair has no TRE yet: why it cannot be measured, or which half is still unmarked.
function pairNote(
    pair: LandmarkPair,
    residual: LandmarkResidual | undefined,
    intl: IntlShape,
    textOf: (residual: LandmarkResidual) => string
): string {
    if (residual) {
        return textOf(residual);
    }
    if (isCompleteLandmarkPair(pair)) {
        return intl.formatMessage({ id: 'landmark.hint.needDeformation' });
    }
    return intl.formatMessage(
        { id: 'landmark.awaiting' },
        {
            image: intl.formatMessage({
                id: pair.fixed ? 'landmark.onMoving' : 'landmark.onFixed',
            }),
        }
    );
}

interface LandmarkRowProps {
    pair: LandmarkPair;
    textOf: (residual: LandmarkResidual) => string;
    name: string;
    defaultColor: DicomColor;
    rigid?: LandmarkResidual;
    deformable?: LandmarkResidual;
    selected: boolean;
    editing: boolean;
    onSelect: (id: string) => void;
    onStartRename: (id: string) => void;
    onRename: (id: string, name: string) => void;
    onRecolor: (id: string, color: DicomColor) => void;
    onEndRename: () => void;
    onToggleVisibility: (id: string) => void;
    onRemove: (id: string) => void;
}

function LandmarkRow({
    pair,
    textOf,
    name,
    defaultColor,
    rigid,
    deformable,
    selected,
    editing,
    onSelect,
    onStartRename,
    onRename,
    onRecolor,
    onEndRename,
    onToggleVisibility,
    onRemove,
}: LandmarkRowProps) {
    const intl = useIntl();
    const measured =
        rigid?.status === 'measured' || deformable?.status === 'measured';

    if (editing) {
        return (
            <TextField
                size="small"
                autoFocus
                fullWidth
                defaultValue={pair.name ?? ''}
                placeholder={name}
                inputProps={{
                    maxLength: LANDMARK_NAME_MAX_LENGTH,
                    'aria-label': intl.formatMessage({ id: 'landmark.rename' }),
                    'data-testid': 'landmark-name-input',
                    onKeyDown: (event) => {
                        if (event.key === 'Enter') {
                            onRename(pair.id, event.currentTarget.value);
                            onEndRename();
                        } else if (event.key === 'Escape') {
                            // Cancels the rename, not the tool.
                            event.stopPropagation();
                            onEndRename();
                        }
                    },
                }}
                onBlur={(event) => {
                    onRename(pair.id, event.target.value);
                    onEndRename();
                }}
                sx={{ my: 0.25, '& input': { fontSize: 12, py: 0.5 } }}
            />
        );
    }

    return (
        <Box
            data-testid={`landmark-row-${pair.id}`}
            sx={{
                py: 0.25,
                mb: 0.25,
                borderRadius: 1,
                bgcolor: selected ? 'action.selected' : undefined,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ColorSwatchPicker
                    color={pair.color ?? defaultColor}
                    onChange={(color) => onRecolor(pair.id, color)}
                    ariaLabel={intl.formatMessage(
                        { id: 'landmark.recolor' },
                        { name }
                    )}
                    dataTestId={`landmark-color-${pair.id}`}
                    size={14}
                />
                <ButtonBase
                    onClick={() => onSelect(pair.id)}
                    title={name}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        justifyContent: 'flex-start',
                        px: 0.5,
                        py: 0.25,
                        borderRadius: 1,
                    }}
                >
                    <Typography variant="caption" noWrap>
                        {name}
                    </Typography>
                </ButtonBase>
                <Stack direction="row">
                    <Tooltip title={<FormattedMessage id="landmark.rename" />}>
                        <IconButton
                            size="small"
                            onClick={() => onStartRename(pair.id)}
                            data-testid="landmark-rename"
                        >
                            <Pencil size={13} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title={
                            <FormattedMessage id="landmark.toggleVisibility" />
                        }
                    >
                        <IconButton
                            size="small"
                            onClick={() => onToggleVisibility(pair.id)}
                        >
                            {pair.visible ? (
                                <Eye size={13} />
                            ) : (
                                <EyeOff size={13} />
                            )}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={<FormattedMessage id="landmark.remove" />}>
                        <IconButton
                            size="small"
                            onClick={() => onRemove(pair.id)}
                        >
                            <Trash2 size={13} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>
            {measured ? (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    data-testid="landmark-row-tre"
                    sx={{ display: 'block', fontSize: 10, pl: 3 }}
                >
                    <FormattedMessage
                        id="landmark.rowTre"
                        values={{
                            rigid: cmOf(rigid),
                            deformable: cmOf(deformable),
                        }}
                    />
                </Typography>
            ) : (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    data-testid="landmark-row-note"
                    sx={{ display: 'block', fontSize: 10, pl: 3 }}
                >
                    {pairNote(pair, deformable ?? rigid, intl, textOf)}
                </Typography>
            )}
        </Box>
    );
}

function MeanRow({
    labelId,
    measuredMm,
}: {
    labelId: string;
    measuredMm: readonly number[];
}) {
    const aggregate = sampleAggregate([...measuredMm]);
    if (!aggregate) {
        return null;
    }
    return (
        <Typography
            variant="caption"
            sx={{ display: 'block' }}
            data-testid={`landmark-mean-${labelId}`}
        >
            <FormattedMessage
                id={
                    labelId === 'rigid'
                        ? 'landmark.meanRigid'
                        : 'landmark.meanDeformable'
                }
                values={{ value: formatMeanWithSpreadCm(aggregate) }}
            />
        </Typography>
    );
}

function Means({ residuals }: { residuals: RunLandmarkResiduals }) {
    const { rigid, deformable, scale } = residuals;
    const anyMeasured =
        (rigid?.measuredMm.length ?? 0) > 0 ||
        (deformable?.measuredMm.length ?? 0) > 0;
    if (!anyMeasured) {
        return null;
    }
    return (
        <Box data-testid="landmark-means">
            <MeanRow labelId="rigid" measuredMm={rigid?.measuredMm ?? []} />
            <MeanRow
                labelId="deformable"
                measuredMm={deformable?.measuredMm ?? []}
            />
            <ResidualScaleNote scale={scale} testIdPrefix="landmark" />
        </Box>
    );
}

export default function LandmarkPanel() {
    const intl = useIntl();
    const theme = useTheme();
    // decomposeColor: the theme's palette is hex, which the regex helper rejects.
    const defaultLandmarkColor = useMemo(() => {
        const [R, G, B] = decomposeColor(theme.palette.secondary.main).values;
        return { R, G, B };
    }, [theme.palette.secondary.main]);
    const {
        pairs,
        awaiting,
        removePair,
        renamePair,
        recolorPair,
        toggleVisibility,
        toggleAllVisibility,
        clearAll,
        selectedPairId,
        togglePair,
    } = useLandmarkController();
    const { confirming: confirmingClear, requestClear } = useConfirmClear(
        pairs.length,
        clearAll
    );
    const landmarkStructures = useAddLandmarksToStructureSet();
    const comparisonOpen = useComparisonImages().length > 0;
    const frames = useSelectedRegistrationFrames();
    const placeableFrames = usePlaceableFramesOfReference();
    const residuals = useRunLandmarkResiduals();
    const { nameOf, textOf } = useLandmarkReadout();
    const setTool = useSetRecoilState(selectedToolState);
    const [editingId, setEditingId] = useState<string | null>(null);
    const anyVisible = pairs.some((pair) => pair.visible);

    const rigidById: ByPairId = useMemo(
        () =>
            new Map(
                (residuals.rigid?.residuals ?? []).map((r) => [r.pairId, r])
            ),
        [residuals.rigid]
    );
    const deformableById: ByPairId = useMemo(
        () =>
            new Map(
                (residuals.deformable?.residuals ?? []).map((r) => [
                    r.pairId,
                    r,
                ])
            ),
        [residuals.deformable]
    );

    // Checked by frame, not panel: a deformed series sits in the fixed frame and refuses points.
    const wantedFrame =
        frames && (awaiting === 'fixed' ? frames.fixed : frames.moving);
    const blocked = !wantedFrame || !placeableFrames.has(wantedFrame);
    const hintId = !frames
        ? 'landmark.hint.needDeformation'
        : blocked
          ? awaiting === 'fixed'
              ? 'landmark.hint.needPrimaryImage'
              : !comparisonOpen
                ? 'landmark.hint.needSideBySide'
                : 'landmark.hint.needMovingImage'
          : awaiting === 'fixed'
            ? 'landmark.hint.fixed'
            : 'landmark.hint.moving';

    return (
        <Stack gap={0.5} sx={{ p: 1 }}>
            <Box>
                <Button
                    size="small"
                    startIcon={<ChevronLeft size={14} />}
                    onClick={() => setTool('pan')}
                    data-testid="landmark-close-panel"
                    sx={{ fontSize: 11, textTransform: 'none' }}
                >
                    <FormattedMessage id="landmark.closePanel" />
                </Button>
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                <FormattedMessage id="landmark.title" />
            </Typography>
            <Typography
                variant="caption"
                color={blocked ? 'warning.main' : 'text.secondary'}
            >
                <FormattedMessage id={hintId} />
            </Typography>
            <Means residuals={residuals} />
            {pairs.length > 0 && (
                <>
                    <Divider sx={{ my: 0.5 }} />
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 0.5,
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            <FormattedMessage id="landmark.title" />
                        </Typography>
                        <Tooltip
                            title={intl.formatMessage({
                                id: 'landmark.toggleAllVisibility',
                            })}
                        >
                            <IconButton
                                size="small"
                                onClick={toggleAllVisibility}
                                aria-pressed={anyVisible}
                                data-testid="landmark-toggle-all-visibility"
                            >
                                {anyVisible ? (
                                    <Eye size={14} />
                                ) : (
                                    <EyeOff size={14} />
                                )}
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box
                        sx={{
                            maxHeight: LIST_MAX_HEIGHT_PX,
                            overflowY: 'auto',
                            // Set explicitly, or it computes to `auto` and shows a horizontal bar.
                            overflowX: 'hidden',
                        }}
                    >
                        {pairs.map((pair, index) => (
                            <LandmarkRow
                                key={pair.id}
                                pair={pair}
                                textOf={textOf}
                                defaultColor={defaultLandmarkColor}
                                name={nameOf(pair, index)}
                                rigid={rigidById.get(pair.id)}
                                deformable={deformableById.get(pair.id)}
                                selected={pair.id === selectedPairId}
                                editing={pair.id === editingId}
                                onSelect={togglePair}
                                onStartRename={setEditingId}
                                onRename={renamePair}
                                onRecolor={recolorPair}
                                onEndRename={() => setEditingId(null)}
                                onToggleVisibility={toggleVisibility}
                                onRemove={removePair}
                            />
                        ))}
                    </Box>
                    {landmarkStructures.count > 0 && (
                        <Box sx={{ pt: 0.5 }}>
                            <Button
                                size="small"
                                variant="outlined"
                                fullWidth
                                onClick={() => void landmarkStructures.add()}
                                data-testid="landmark-add-to-structure-set"
                            >
                                <FormattedMessage
                                    id="landmark.addToStructureSet"
                                    values={{ count: landmarkStructures.count }}
                                />
                            </Button>
                        </Box>
                    )}
                    <Box sx={{ pt: 0.5 }}>
                        <Button
                            size="small"
                            color={confirmingClear ? 'error' : 'inherit'}
                            onClick={requestClear}
                            fullWidth
                        >
                            <FormattedMessage
                                id={
                                    confirmingClear
                                        ? 'landmark.clearAllConfirm'
                                        : 'landmark.clearAll'
                                }
                            />
                        </Button>
                    </Box>
                </>
            )}
        </Stack>
    );
}
